import { escapeHtml, htmlPieces } from "./simpleHtml";

/** The marker of a picture in the text (the pictures-in-the-text round, 2026-10-29; brief docs/briefs/pictures-in-the-text-round.md rule 2,
 *  the red team's findings 3 and 4). A message's stored HTML holds `<p>{attachment:<id>}</p>` where one of its own files is shown in the
 *  text; every reader turns it into its own thing (a picture in the thread, an inline part in the mail, a signed picture in the message
 *  centre, the words "[Picture: name]" everywhere else). This module is the one place the pattern lives: the editor, the store, the mail,
 *  the thread, the portal and the connector all read it from here. Pure. */

const UUID = "[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}";
/** one marker anywhere in a text; the id is the first group. Global: reset `lastIndex` or use the helpers below. */
export const ATTACHMENT_MARKER = new RegExp(`\\{attachment:(${UUID})\\}`, "g");
/** a paragraph that holds nothing but one marker (the stored form) */
const MARKER_PARAGRAPH = new RegExp(`<p>\\s*\\{attachment:(${UUID})\\}\\s*</p>`, "g");
/** a line of Markdown that holds nothing but one marker (how the connector's AI places a picture) */
const MARKER_LINE = new RegExp(`^[ \\t]*\\{attachment:(${UUID})\\}[ \\t]*$`, "gm");
/** something that looks like a marker but is no attachment id (an invented code the AI must be told about) */
const MARKER_LIKE = /\{attachment:([^}\s]{1,80})\}/g;

/** the marker's text for an attachment id */
export const attachmentMarker = (id: string): string => `{attachment:${id}}`;
/** the stored paragraph of a picture */
export const markerParagraph = (id: string): string => `<p>${attachmentMarker(id)}</p>`;
/** the words a picture reads as wherever it cannot be shown */
export const pictureWords = (name: string): string => `[Picture: ${name}]`;

/** the ids a text's markers name, in order, each once */
export function markedAttachmentIds(text: string): string[] {
  const out: string[] = [];
  for (const m of text.matchAll(ATTACHMENT_MARKER)) if (!out.includes(m[1]!)) out.push(m[1]!);
  return out;
}

/** every marker-like code of a Markdown text (an attachment id or not), each once: the connector refuses those it cannot place */
export function markerLikeCodes(text: string): string[] {
  const out: string[] = [];
  for (const m of text.matchAll(MARKER_LIKE)) if (!out.includes(m[1]!)) out.push(m[1]!);
  return out;
}

/** each marker paragraph replaced by what `fn` answers for its id; `null` keeps the paragraph as it is */
export function replaceMarkerParagraphs(html: string, fn: (id: string) => string | null): string {
  return html.replace(MARKER_PARAGRAPH, (whole, id: string) => fn(id) ?? whole);
}

/** each Markdown line that is one marker replaced by what `fn` answers for its id; `null` keeps the line */
export function replaceMarkerLines(markdown: string, fn: (id: string) => string | null): string {
  return markdown.replace(MARKER_LINE, (whole, id: string) => fn(id) ?? whole);
}

/** each marker anywhere replaced by what `fn` answers; `null` keeps it */
export function replaceMarkers(text: string, fn: (id: string) => string | null): string {
  return text.replace(ATTACHMENT_MARKER, (whole, id: string) => fn(id) ?? whole);
}

/** the marker paragraphs of an HTML text read as "[Picture: name]" paragraphs, for the pictures named (a site, the connector, the search) */
export function markersAsWords(html: string, names: Readonly<Record<string, string>>): string {
  return replaceMarkerParagraphs(html, (id) => (names[id] === undefined ? null : `<p>${escapeHtml(pictureWords(names[id]!))}</p>`));
}

/** The composer's pictures (the editor's `<img data-attachment="<id>">`), turned into the stored marker before the HTML is cleaned (the
 *  red team's finding 3): a picture whose id is one of `own` (this message's files) becomes its marker paragraph, every other picture (an
 *  outside address, another file, no id) leaves with its tag. Answers the HTML and the ids placed, in order, each once. */
export function composerPicturesToMarkers(html: string, own: ReadonlySet<string>): { html: string; ids: string[] } {
  const ids: string[] = [];
  let out = "";
  for (const p of htmlPieces(html)) {
    if (p.kind !== "tag" || p.name !== "img") { out += p.raw; continue; }
    const id = p.attrs.find((a) => a.name === "data-attachment")?.value.trim() ?? "";
    if (!own.has(id)) continue;
    if (!ids.includes(id)) ids.push(id);
    out += markerParagraph(id);
  }
  return { html: ids.length ? unwrapMarkerParagraphs(out) : out, ids };
}

/** the ids of the composer's own pictures in an HTML text (the editor's `data-attachment`), in order, each once: what Send attaches */
export function composerPictureIds(html: string): string[] {
  const ids: string[] = [];
  for (const p of htmlPieces(html)) {
    if (p.kind !== "tag" || p.name !== "img") continue;
    const id = p.attrs.find((a) => a.name === "data-attachment")?.value.trim() ?? "";
    if (new RegExp(`^${UUID}$`).test(id) && !ids.includes(id)) ids.push(id);
  }
  return ids;
}

/** a marker paragraph the editor left inside a paragraph (`<p>words<img></p>`) stands alone: the paragraph is split around it, and the
 *  empty paragraph the split leaves behind goes */
function unwrapMarkerParagraphs(html: string): string {
  return html
    .replace(new RegExp(`<p>\\s*(<p>\\{attachment:${UUID}\\}</p>)\\s*</p>`, "g"), "$1")
    .replace(new RegExp(`<p>([^<]*?)(<p>\\{attachment:${UUID}\\}</p>)`, "g"), (_w, before: string, marker: string) => (before.trim() ? `<p>${before}</p>${marker}<p>` : `${marker}<p>`))
    .replace(new RegExp(`(<p>\\{attachment:${UUID}\\}</p>)<p>\\s*</p>`, "g"), "$1");
}
